import { Stack, Typography, Box, useTheme, Chip } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { CheckCircleOutline, CancelOutlined } from '@mui/icons-material';
import Color from 'color';

/* ── Hooks ───────────────────────────────────────────────────── */

function useVersionInfo() {
    const [version, setVersion] = useState<{ version: string; buildTime: string } | null>(null);
    useEffect(() => {
        fetch('/version.json')
            .then((r) => r.json())
            .then((data) => setVersion(data))
            .catch(() => setVersion({ version: 'unknown', buildTime: 'unknown' }));
    }, []);
    return version;
}

function useBrowserCapabilities() {
    return useMemo(() => {
        const canvas = document.createElement('canvas');
        const webgl = !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
        const webgpu = 'gpu' in navigator;
        const webnn = 'ml' in navigator;
        return { webgl, webgpu, webnn };
    }, []);
}

/* ── Component ───────────────────────────────────────────────── */

const AboutTab: React.FC = () => {
    const muiTheme = useTheme();
    const accent = muiTheme.palette.primary.main;
    const versionInfo = useVersionInfo();
    const browserCaps = useBrowserCapabilities();

    return (
        <Stack spacing={2.5}>
            {/* Branding */}
            <Box
                sx={{
                    px: 2.5,
                    py: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: Color(accent).alpha(0.3).hexa(),
                    background: `linear-gradient(135deg, ${Color(accent).alpha(0.08).hexa()} 0%, ${Color(accent).alpha(0.02).hexa()} 100%)`,
                    textAlign: 'center',
                }}
            >
                <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>
                    eConnect Face Verify
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Made by{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: accent }}>eConnect, Inc.</Box>
                </Typography>
            </Box>

            {/* Version info */}
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                Version
            </Typography>

            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: Color(accent).alpha(0.3).hexa(),
                    backgroundColor: Color(accent).alpha(0.04).hexa(),
                }}
            >
                {versionInfo ? (
                    <Stack spacing={1}>
                        {[
                            { label: 'Version', value: versionInfo.version },
                            { label: 'Build Time', value: versionInfo.buildTime },
                        ].map(({ label, value }) => (
                            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" fontWeight={500}>{label}</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: accent }}>{value}</Typography>
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary">Loading version info…</Typography>
                )}
            </Box>

            {/* Browser capabilities */}
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                Browser Capabilities
            </Typography>

            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: Color(accent).alpha(0.3).hexa(),
                    backgroundColor: Color(accent).alpha(0.04).hexa(),
                }}
            >
                <Stack spacing={1.5}>
                    {[
                        { label: 'WebGL', supported: browserCaps.webgl, desc: 'GPU-accelerated graphics for model inference' },
                        { label: 'WebGPU', supported: browserCaps.webgpu, desc: 'Next-gen GPU compute for faster inference' },
                        { label: 'WebNN', supported: browserCaps.webnn, desc: 'Native neural network hardware acceleration' },
                    ].map(({ label, supported, desc }) => (
                        <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" fontWeight={500}>{label}</Typography>
                                <Typography variant="caption" color="text.secondary">{desc}</Typography>
                            </Box>
                            <Chip
                                size="small"
                                icon={supported
                                    ? <CheckCircleOutline sx={{ fontSize: 16 }} />
                                    : <CancelOutlined sx={{ fontSize: 16 }} />
                                }
                                label={supported ? 'Supported' : 'Unavailable'}
                                sx={{
                                    fontWeight: 500,
                                    borderRadius: 1.5,
                                    backgroundColor: supported
                                        ? Color('#4caf50').alpha(0.12).hexa()
                                        : Color('#fff').alpha(0.06).hexa(),
                                    color: supported ? '#66bb6a' : 'text.secondary',
                                    border: '1px solid',
                                    borderColor: supported
                                        ? Color('#4caf50').alpha(0.3).hexa()
                                        : Color('#fff').alpha(0.1).hexa(),
                                    '& .MuiChip-icon': {
                                        color: supported ? '#66bb6a' : 'text.secondary',
                                    },
                                }}
                            />
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Stack>
    );
};

export default AboutTab;
